import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

// components
import CardTitlePart from '../../parts/CardTitlePart';
import HandlePaymentCard from './payBalanceSections/HandlePaymentCard';
import HtmlPart from '../../parts/HtmlPart';

// actions
import { getAmountAndDetailsForOrder } from '../../actions/payment';
import { getEvents, pushEventQueue } from '../../actions/cardEvents';

// services
import { gtmSimplePageLoadEvent } from '../../services/gtmService';
import { isDeliveryAlreadyBooked } from '../../helpers';
import OverlayLoader from '../../parts/OverlayLoader';

const MINIMUM_AMOUNT_TO_PAY = 15;

class _PaymentCard extends Component {

  constructor(props) {
    super(props);
    this.state = {
      totalBalance: null,
      loadingAmount: true,
      amountToPay: null,
      paymentAmountError: null,
      paymentType: 'standard',
      isPaymentAvailable: this.isPaymentAvailable()
    };
    this.checkAndUpdateAmount = this.checkAndUpdateAmount.bind(this);
    this.checkAndUpdateAmount();

    if (props.parentCardName && props.parentCardName === 'DeliveryFlow') {
      gtmSimplePageLoadEvent(`${props.parentCardName} page ${props.thisCardName}`);
    }
  }

  onSofologistSelectPaymentType(paymentType) {
    this.setState({ paymentType });
  }

  handleChangeAmountToPay(e) {
    const { totalBalance } = this.state;
    const value = e.target.value;

    const allowedPattern = /^\d*\.?\d{0,2}$/;
    if (!allowedPattern.test(value)) {
      return;
    }

    this.setState({
      amountToPay: value,
      paymentType: 'none'
    });

    if (!value) {
      this.setState({ paymentAmountError: 'Please pay your balance' });
      return;
    }

    const numericValue = parseFloat(value);
    const numericTotal = parseFloat(totalBalance);
    const hasDot = value.includes('.');

    if (!isNaN(numericValue) && numericValue < MINIMUM_AMOUNT_TO_PAY) {
      this.setState({
        paymentAmountError: `Minimum payment is £${MINIMUM_AMOUNT_TO_PAY.toFixed(2)}`
      });
      return;
    }

    if (!isNaN(numericValue) && numericValue > numericTotal) {
      this.setState({
        paymentAmountError: 'Balance to pay should be less than balance amount'
      });
      return;
    }

    const isEqualToTotal =
      !isNaN(numericValue) &&
      numericValue.toFixed(2) === numericTotal.toFixed(2);

    if (hasDot && (value.endsWith('.') || !isEqualToTotal)) {
      this.setState({
        paymentAmountError: 'Enter whole £ amounts for smaller instalments'
      });
      return;
    }

    this.setState({ paymentAmountError: null });
  }

  componentDidUpdate(prevProps, prevState) {
    const isLoadingFinished = prevState.loadingAmount && !this.state.loadingAmount;

    if (this.isDeliveryBookingFlow() && isLoadingFinished) {
      const { orderData, amountToPay } = this.state;
      const { OrderDetails } = orderData;

      // Order can be already paid during card initialization, or paid via this card
      const isOrderPaid = (prevState.amountToPay === null && amountToPay === 0)
        || (prevState.amountToPay > 0 && amountToPay === 0);

      const orderStatus = orderData && OrderDetails && OrderDetails.Status;

      if (isOrderPaid) {
        this.finilizeDeliveryBookingFlow(orderStatus);
      }

      this.props.passToFlowState({ outstandingBalance: !isOrderPaid, orderStatus });
    }
  }

  finilizeDeliveryBookingFlow(orderStatus) {
    const { orderIdx, groupId, deliveryBookedAt, getEvents, pushQueue, pushEventQueue } = this.props;

    // isDeliveryAlreadyBooked - just needs a time out to send booking requests.
    // 5 minutes to synchronise data between services.
    if (groupId && !!parseInt(pushQueue, 10) && !isDeliveryAlreadyBooked(deliveryBookedAt)) {

      pushEventQueue(orderIdx).then(getEvents);
    }
  }

  checkAndUpdateAmount() {
    const { getAmountAndDetailsForOrder, orderIdx } = this.props;

    if (this.state.loadingAmount === false) {
      this.setState({ loadingAmount: true });
    }

    return getAmountAndDetailsForOrder(orderIdx).then((res) => {
      if (res && res.data) {
        const updatedBalance = res.data.Balance;

        const { totalBalance, amountToPay, paymentType } = this.state;

        const isBalanceChanged = totalBalance !== updatedBalance;

        const stateUpdate = {
          totalBalance: updatedBalance,
          amountToPay: isBalanceChanged ? updatedBalance : amountToPay,
          orderData: res.data,
          paymentType: isBalanceChanged ? 'standard' : paymentType,
          loadingAmount: false
        };

        this.setState(stateUpdate);

        return stateUpdate;
      }

      return Promise.reject('failed to get balance');
    });
  }

  renderPaymentBalanceInput = () => {
    const { amountToPay, paymentAmountError } = this.state;

    const isInvalidAmount = !!paymentAmountError;

    return (
      <div className="payment-input">
        <label htmlFor='payment-input' className="h4 u-text-center u-margin-reset payment-input__label">Balance to pay:</label>
        <div className="payment-input__input-container">
          <input
            id='payment-input'
            className={`payment-input__input-container__input ${isInvalidAmount ? 'error' : ''}`}
            type="text"
            maxLength={6}
            value={amountToPay}
            onChange={e => this.handleChangeAmountToPay(e)}
          />
          <div className={`payment-input__input-container__currency-symbol ${isInvalidAmount ? 'error' : ''}`}>£</div>
        </div>
        {isInvalidAmount ?
          <div className="payment-input__input_error  u-text-center">
            {paymentAmountError}
          </div>
          : <div className="payment-input__input_info u-text-center">
            {'Pay the full amount or in smaller instalments'}
          </div>
        }
      </div>
    );
  }

  renderPaymentSection() {
    const { orderIdx, orderRef, zeroBalanceText, parentCardName, orderNo } = this.props;
    const { totalBalance, amountToPay, loadingAmount, paymentType, orderData, isPaymentAvailable, paymentAmountError } = this.state;

    const showInputPayment = !loadingAmount && !!totalBalance && totalBalance >= MINIMUM_AMOUNT_TO_PAY;
    const payBalanceInputComponent = !this.isDeliveryBookingFlow() && showInputPayment
      ? this.renderPaymentBalanceInput
      : () => null;

    if (totalBalance === 0) {
      return (
        <div className="u-padding-y u-text-center h4 u-pale-grey-bg u-padding-x">
          <HtmlPart htmlString={zeroBalanceText || 'Great news! Your order has been fully paid for and you&#39;ll soon be feeling at home on a sofa you love'} />
        </div>
      );
    }

    return (
      <HandlePaymentCard
        flowName={parentCardName}
        totalBalance={totalBalance}
        paymentTypeButtonDisabled={!!paymentAmountError}
        payBalanceInputComponent={payBalanceInputComponent}
        amountToPay={amountToPay}
        paymentType={paymentType}
        setPaymentType={type => this.setState({ paymentType: type })}
        loadingAmount={loadingAmount}
        orderIdx={orderIdx}
        orderRef={orderRef}
        orderNo={orderNo}
        orderData={orderData}
        isPaymentAvailable={isPaymentAvailable}
        onCheckUpdateAmount={this.checkAndUpdateAmount}
      />
    );
  }

  renderSofologistPaymentTypeSelector() {
    const { totalBalance, loadingAmount, paymentAmountError } = this.state;

    if (!this.props.isSofologist) return null;
    if (totalBalance <= 0 && !loadingAmount) return null;

    const SelectorTab = (text, paymentType) => (
      <button
        className={`c-tab-bar__tab  c-tab-bar__tab__footer ${this.state.paymentType === paymentType ? 'c-tab-bar__tab__footer--active  c-tab-bar__tab__footer--active-right' : ''} u-padding-small`}
        disabled={!!paymentAmountError}
        onClick={() => this.onSofologistSelectPaymentType(paymentType)}
      >
        {text}
      </button>
    );

    const NoPaymentTab =
      (<div className="u-padding-bottom-small u-text-center c-event-feed__card__extra-info">
        <strong>Let the customer complete the payment through his mysofology personal account</strong>
      </div>);

    return (
      <div className="c-tab-bar" >
        {this.isRetailStore() && SelectorTab('CARD', 'standard')}
        {this.isRetailStore() && SelectorTab('PDQ', 'pdq')}
        {this.isRetailStore() && this.props.cashPaymentEnabled && SelectorTab('CASH', 'cash')}
        {!this.isPaymentAvailable() && NoPaymentTab}
      </div>
    );
  }

  render() {
    const { title, orderRef, orderNo, isSofologist, loadingAmount } = this.props;

    return (
      <div>
        <CardTitlePart title={title} />
        {loadingAmount ? <OverlayLoader active /> : this.renderPaymentSection()}
        <div className="u-padding-bottom-small u-text-center c-event-feed__card__extra-info">
          <strong>Order Number:</strong> {`${orderRef}/${orderNo}`}
        </div>
        {isSofologist && this.renderSofologistPaymentTypeSelector()}
      </div>
    );
  }

  isPaymentAvailable() {
    const isPaymentAvailableForSofologist = this.props.isSofologist && this.isRetailStore();
    return !this.props.isSofologist || isPaymentAvailableForSofologist;
  }

  isRetailStore() {
    return this.props.isSofologist && !this.props.isTelStore;
  }

  isDeliveryBookingFlow() {
    return this.props.thisCardName === 'Payment';
  }
}

const mapStateToProps = state => ({
  isSofologist: state.sofologist.Authorized,
  location: state.sofologist.location,
  isTelStore: state.sofologist.isTelStore,
  cashPaymentEnabled: state.sofologist.cashPaymentEnabled
});

const mapDispatchToProps = dispatch => bindActionCreators({
  pushEventQueue, getEvents, getAmountAndDetailsForOrder
}, dispatch);


export default connect(mapStateToProps, mapDispatchToProps)(_PaymentCard);



// WEBPACK FOOTER //
// ./src/cards/templates/PayBalanceCard.js